import React, { useMemo } from "react";
import { CaptionStyles } from "../../../types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Slider } from "../../ui/slider";
import {
  TEXT_SHADOW_PRESETS,
  TextShadowPresetKey,
} from "../../../templates/caption-templates";

interface CaptionEffectsSettingsProps {
  styles: CaptionStyles;
  onStyleChange: (field: keyof CaptionStyles, value: any) => void;
  onHighlightStyleChange: (
    field: keyof NonNullable<CaptionStyles["highlightStyle"]>,
    value: any
  ) => void;
}

/**
 * CaptionEffectsSettings Component
 *
 * Provides controls for visual effects:
 * - Highlight Scale (slider 1.0 - 1.2)
 * - Text Shadow (preset dropdown)
 * - Border Radius (slider 0px - 20px)
 */
export const CaptionEffectsSettings: React.FC<CaptionEffectsSettingsProps> = ({
  styles,
  onStyleChange,
  onHighlightStyleChange,
}) => {
  // Get current scale value
  const currentScale = styles.highlightStyle?.scale ?? 1.06;

  // Parse current border radius to px value
  const currentBorderRadiusPx = useMemo(() => {
    const br = styles.highlightStyle?.borderRadius;
    if (!br) return 6;
    const numeric = parseFloat(br);
    if (Number.isNaN(numeric)) return 6;
    return numeric;
  }, [styles.highlightStyle?.borderRadius]);

  // Determine current shadow preset based on textShadow value
  const currentShadowPreset = useMemo((): TextShadowPresetKey => {
    const currentShadow = styles.textShadow || "";

    // Match against presets
    for (const [key, value] of Object.entries(TEXT_SHADOW_PRESETS)) {
      if (currentShadow === value) {
        return key as TextShadowPresetKey;
      }
    }

    // If no exact match, check for partial matches or return "soft" as default
    if (!currentShadow || currentShadow === "") return "none";
    if (currentShadow.includes("rgba(0,0,0,0.4)") || currentShadow.includes("0.4"))
      return "soft";
    if (currentShadow.includes("0 0 10px")) return "glow";
    if (currentShadow.includes("0 rgba(0,0,0,0.8)") || currentShadow.includes("0.8"))
      return "hard";

    return "soft"; // Default fallback
  }, [styles.textShadow]);

  return (
    <div className="space-y-4 rounded-md bg-sidebar p-2.5 border min-w-0 w-full max-w-full">
      <h5 className="text-sm font-light leading-none text-foreground">Effects</h5>

      {/* Highlight Scale */}
      <div className="space-y-2 min-w-0 w-full">
        <div className="flex items-center justify-between">
          <label className="text-xs text-muted-foreground/50">Highlight Scale</label>
          <span className="text-xs text-muted-foreground">
            {currentScale.toFixed(2)}x
          </span>
        </div>
        <div className="w-full min-w-0">
          <Slider
            value={[currentScale]}
            onValueChange={(value) => onHighlightStyleChange("scale", value[0])}
            min={1.0}
            max={1.2}
            step={0.01}
            className="w-full"
          />
        </div>
      </div>

      {/* Text Shadow */}
      <div className="space-y-2 min-w-0 w-full">
        <label className="text-xs text-muted-foreground/50">Text Shadow</label>
        <Select
          value={currentShadowPreset}
          onValueChange={(value: TextShadowPresetKey) => {
            onStyleChange("textShadow", TEXT_SHADOW_PRESETS[value]);
          }}
        >
          <SelectTrigger className="w-full text-xs text-foreground">
            <SelectValue placeholder="Select shadow" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none" className="text-xs">
              None
            </SelectItem>
            <SelectItem value="soft" className="text-xs">
              Soft
            </SelectItem>
            <SelectItem value="hard" className="text-xs">
              Hard
            </SelectItem>
            <SelectItem value="glow" className="text-xs">
              Glow
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Border Radius */}
      <div className="space-y-2 min-w-0 w-full">
        <div className="flex items-center justify-between">
          <label className="text-xs text-muted-foreground/50">Border Radius</label>
          <span className="text-xs text-muted-foreground">
            {currentBorderRadiusPx}px
          </span>
        </div>
        <div className="w-full min-w-0">
          <Slider
            value={[currentBorderRadiusPx]}
            onValueChange={(value) =>
              onHighlightStyleChange("borderRadius", `${value[0]}px`)
            }
            min={0}
            max={20}
            step={1}
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
};
